import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useController } from 'react-hook-form';
import InputField from '../InputField';

const parseDate = date => {
  const [year, month, day] = date.split('-');
  return { year: year || '', month: month || '', day: day || '' };
};

const transform = {
  output: ({ day, month, year }) => {
    if (day && month && year)
      return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    return '';
  },
  input: ({ day = '', month = '', year = '' }) => {
    const parsedDay = day.replace(/\D/g, '');
    const parsedMonth = month.replace(/\D/g, '');
    const parsedYear = year.replace(/\D/g, '');
    return { day: parsedDay, month: parsedMonth, year: parsedYear };
  },
};

const DateOfBirthField = ({
  name,
  state,
  control,
  defaultValue,
  placeholders,
  readOnly,
  rules,
}) => {
  const [currentValues, setCurrentValues] = useState(parseDate(defaultValue));

  const {
    field: { ref, ...field },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister: true,
  });

  const updateDateOfBirthValue = useCallback(
    values => field.onChange(transform.output(values)),
    [field],
  );

  const handleDayChange = event => {
    const { value } = event.target;
    const val = value.trim();
    const parsedValues = transform.input({
      day: val,
      month: currentValues.month,
      year: currentValues.year,
    });
    updateDateOfBirthValue(parsedValues);
    setCurrentValues(parsedValues);
  };

  const handleMonthChange = event => {
    const { value } = event.target;
    const val = value.trim();
    const parsedValues = transform.input({
      day: currentValues.day,
      month: val,
      year: currentValues.year,
    });
    updateDateOfBirthValue(parsedValues);
    setCurrentValues(parsedValues);
  };

  const handleYearChange = event => {
    const { value } = event.target;
    const val = value.trim();
    const parsedValues = transform.input({
      day: currentValues.day,
      month: currentValues.month,
      year: val,
    });
    updateDateOfBirthValue(parsedValues);
    setCurrentValues(parsedValues);
  };

  return (
    <>
      <InputField
        name="day"
        onChange={handleDayChange}
        onBlur={field.onBlur}
        inputClassName="text-center w-20"
        id="day"
        state={state}
        type="text"
        placeholder={placeholders.day}
        inputMode="numeric"
        maxLength={2}
        value={currentValues.day}
        readOnly={readOnly}
      />
      <InputField
        name="month"
        onChange={handleMonthChange}
        onBlur={field.onBlur}
        inputClassName="text-center w-20"
        id="month"
        state={state}
        type="text"
        placeholder={placeholders.month}
        inputMode="numeric"
        maxLength={2}
        value={currentValues.month}
        readOnly={readOnly}
      />
      <InputField
        name="year"
        onChange={handleYearChange}
        onBlur={field.onBlur}
        inputClassName="text-center w-28"
        id="year"
        state={state}
        type="text"
        placeholder={placeholders.year}
        inputMode="numeric"
        maxLength={4}
        value={currentValues.year}
        readOnly={readOnly}
      />
    </>
  );
};

DateOfBirthField.defaultProps = {
  defaultValue: '',
  readOnly: false,
  placeholders: {
    day: 'TT',
    month: 'MM',
    year: 'JJJJ',
  },
  rules: {
    required: true,
  },
};

DateOfBirthField.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  state: PropTypes.oneOf(['default', 'invalid', 'valid', 'widget']).isRequired,
  control: PropTypes.shape().isRequired,
  readOnly: PropTypes.bool,
  placeholders: PropTypes.shape({
    day: PropTypes.string,
    month: PropTypes.string,
    year: PropTypes.string,
  }),
  rules: PropTypes.shape({
    required: PropTypes.bool,
  }),
};

export default DateOfBirthField;
